// ============================================================================
// ContractTemplateDialog — creer / editer un modele de contrat (AD-E)
// ============================================================================
// Editeur du REFERENTIEL de gabarits (nom + corps). Le corps est du texte a
// jetons de fusion (type {{employee_name}}) stocke TEL QUEL : aucun moteur de
// rendu ici, c'est du stockage de gabarit (le rendu a lieu a l'emission, cote
// fiche employe). GsWizardDialog a une seule etape (AD-0, @gs/ui) — parite du
// motif MemberFormDialog, mais sans select ni multi-etapes.
//
// Etat formulaire : corps interne monte uniquement si open — remontage = re-seed
// naturel (edition A -> fermer -> edition B porte les valeurs de B).

import { useState } from 'react';
import { GsWizardDialog, GS_COMPONENTS, type GsWizardStep } from '@gs/ui';
import { useTranslation } from '../../i18n';
import type { AdminContractTemplate, ContractTemplatePayload } from '../../hooks/useAdminContractTemplates';

interface TemplateFormValues {
  name: string;
  body: string;
}

const EMPTY_VALUES: TemplateFormValues = { name: '', body: '' };

export function ContractTemplateDialog({
  open,
  mode,
  template,
  isPending,
  onClose,
  onSubmit,
}: {
  open: boolean;
  mode: 'create' | 'edit';
  /** Cible en edition — ignore en creation. */
  template?: AdminContractTemplate;
  isPending: boolean;
  onClose: () => void;
  onSubmit: (payload: ContractTemplatePayload) => void;
}) {
  if (!open) return null;
  return (
    <ContractTemplateDialogBody
      open={open}
      mode={mode}
      template={template}
      isPending={isPending}
      onClose={onClose}
      onSubmit={onSubmit}
    />
  );
}

function ContractTemplateDialogBody({
  open,
  mode,
  template,
  isPending,
  onClose,
  onSubmit,
}: {
  open: boolean;
  mode: 'create' | 'edit';
  template?: AdminContractTemplate;
  isPending: boolean;
  onClose: () => void;
  onSubmit: (payload: ContractTemplatePayload) => void;
}) {
  const { t } = useTranslation();
  const [values, setValues] = useState<TemplateFormValues>(() =>
    mode === 'edit' && template ? { name: template.name, body: template.body } : EMPTY_VALUES,
  );

  const set = (patch: Partial<TemplateFormValues>) => setValues((v) => ({ ...v, ...patch }));

  const labelCls = 'text-meta font-medium text-slate-600 dark:text-slate-300';
  const hintCls = 'text-meta text-slate-400 dark:text-slate-500';

  const editStep: GsWizardStep = {
    id: 'template',
    title: t('gest:admin.contracts.form.step'),
    canProceed: () => values.name.trim().length > 0 && values.body.trim().length > 0,
    content: (
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="contract-template-name" className={labelCls}>
            {t('gest:admin.contracts.form.name')}
          </label>
          <input
            id="contract-template-name"
            autoFocus
            maxLength={200}
            placeholder={t('gest:admin.contracts.form.namePlaceholder')}
            value={values.name}
            onChange={(e) => set({ name: e.target.value })}
            className={GS_COMPONENTS.input}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="contract-template-body" className={labelCls}>
            {t('gest:admin.contracts.form.body')}
          </label>
          <textarea
            id="contract-template-body"
            rows={12}
            maxLength={100000}
            placeholder={t('gest:admin.contracts.form.bodyPlaceholder')}
            value={values.body}
            onChange={(e) => set({ body: e.target.value })}
            className={`${GS_COMPONENTS.input} resize-y font-mono leading-relaxed`}
          />
          {/* [!] Jetons de fusion stockes tels quels — aucun rendu cote referentiel. */}
          <p className={hintCls}>{t('gest:admin.contracts.form.bodyHint')}</p>
        </div>
      </div>
    ),
  };

  return (
    <GsWizardDialog
      open={open}
      onClose={onClose}
      title={
        mode === 'create'
          ? t('gest:admin.contracts.form.createTitle')
          : t('gest:admin.contracts.form.editTitle', { name: template?.name ?? '' })
      }
      steps={[editStep]}
      isLoading={isPending}
      previousLabel={t('gest:admin.contracts.form.back')}
      nextLabel={t('gest:admin.contracts.form.next')}
      cancelLabel={t('gest:admin.contracts.form.cancel')}
      pendingLabel={t('gest:admin.contracts.form.pending')}
      completeLabel={
        mode === 'create'
          ? t('gest:admin.contracts.form.submitCreate')
          : t('gest:admin.contracts.form.submitEdit')
      }
      onComplete={() => onSubmit({ name: values.name.trim(), body: values.body.trim() })}
    />
  );
}
